import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as roleService from "../../services/role.services";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";
import { useTranslation } from "react-i18next";

const RoleEdit = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const [showdefault, setShowDefault] = useState({});
  const [saveType, setSaveType] = useState("");
  const [permissionList, setPermissionList] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("label_sub_admin"), url: "/admin/staff/list/1" },
    { title: t("label_role"), url: "/admin/role/list/1" },
    { title: t("btn_edit"), url: "" },
  ];

  useEffect(() => {
    roleService.PermissionsOptions().then((response) => {
      setPermissionList(response?.data || []);
    }).catch((error) => {
      console.log("error=====>", error);
    });

    roleService.Details(params.id).then((response) => {
      if (response?.data) {
        setShowDefault(response.data);
        const savedIds = (response.data.permissionIDs || []).map((p) =>
          typeof p === "object" ? p._id : p
        );
        setSelectedPermissionIds(savedIds);
      }
    }).catch((error) => {
      console.log("error=====>", error);
    });
  }, []);

  // Single permission toggle
  const togglePermission = (id) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Group select/deselect
  const toggleGroup = (groupPermissions) => {
    const groupIds = groupPermissions.map((p) => p._id);
    const allSelected = groupIds.every((id) => selectedPermissionIds.includes(id));
    if (allSelected) {
      setSelectedPermissionIds((prev) => prev.filter((id) => !groupIds.includes(id)));
    } else {
      setSelectedPermissionIds((prev) => [...new Set([...prev, ...groupIds])]);
    }
  };

  // Check all / uncheck all
  const toggleAll = () => {
    const allIds = permissionList.map((p) => p._id);
    const allSelected = allIds.every((id) => selectedPermissionIds.includes(id));
    setSelectedPermissionIds(allSelected ? [] : allIds);
  };

  const groupedPermissions = permissionList.reduce((acc, p) => {
    const g = p.group_name || p.groupName || "";
    const key = (g && String(g).trim()) ? String(g).trim() : "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});
  const groupKeys = Object.keys(groupedPermissions).sort();

  const allIds = permissionList.map((p) => p._id);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedPermissionIds.includes(id));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { title: showdefault?.title ?? "" },
    validationSchema: Yup.object().shape({
      title: Yup.string().required(t("label_title_error") || "Title is required").trim(),
    }),
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      const payload = {
        o_id: params.id,
        title: (values.title || "").trim(),
        permissionIDs: selectedPermissionIds,
      };
      roleService.Edit(payload).then((response) => {
        setSubmitting(false);
        if (response.success) {
          Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          globalLoader(false);
          if (saveType !== "Save") navigate("/admin/role/list/1");
        } else {
          Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
        }
        globalLoader(false);
      }).catch(() => globalLoader(false));
    },
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">{t("label_edit_role")}</h6>
                <div className="row row-sm">

                  {/* Title */}
                  <div className="col-md-12 form-group">
                    <label htmlFor="title" className="text-left d-flex">
                      {t("label_title")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="title"
                      id="title"
                      type="text"
                      placeholder={t("placeholder_enter_title") || "Enter the title"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.title}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="title" form={formik} />
                    </span>
                  </div>

                  {/* Permissions */}
                  <div className="col-lg-12 form-group mt-3">
                    <h6 className="main-content-label mb-2">
                      {t("label_permissions")} <span className="requirestar">*</span>
                    </h6>

                    {/* Note */}
                    <h5 className="text-danger mb-3">{t("label_role_permission_note")}</h5>

                    {/* Check All */}
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span className="fw-bold">Check All</span>
                      <input
                        type="checkbox"
                        className="cp"
                        style={{ width: "18px", height: "18px", cursor: "pointer" }}
                        checked={isAllSelected}
                        onChange={toggleAll}
                      />
                    </div>

                    {!permissionList.length ? (
                      <p className="text-muted">{t("msg_no_records")}</p>
                    ) : (
                      groupKeys.map((groupName) => {
                        const groupPermissions = groupedPermissions[groupName];
                        const groupIds = groupPermissions.map((p) => p._id);
                        const isGroupSelected = groupIds.every((id) => selectedPermissionIds.includes(id));

                        return (
                          <div key={groupName} className="mb-4 pb-3 border-bottom">

                            {/* Group header */}
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <h6 className="text-primary mb-0 fw-bold">{groupName}</h6>
                              <input
                                type="checkbox"
                                className="cp"
                                style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                checked={isGroupSelected}
                                onChange={() => toggleGroup(groupPermissions)}
                              />
                            </div>

                            {/* Individual permissions */}
                            <div className="d-flex flex-wrap gap-3 ps-2">
                              {groupPermissions.map((p) => (
                                <div key={p._id} className="d-flex align-items-center gap-2">
                                  <span>{p.title || p.short_slug || p._id}</span>
                                  <input
                                    type="checkbox"
                                    className="cp"
                                    style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                    checked={selectedPermissionIds.includes(p._id)}
                                    onChange={() => togglePermission(p._id)}
                                  />
                                </div>
                              ))}
                            </div>

                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="mt-5">
                    <button type="submit" className="btn btn-info mr-2" onClick={() => setSaveType("Save")}>
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_save")}
                    </button>
                    <button type="submit" className="btn btn-success mr-2" onClick={() => setSaveType("")}>
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_save_exit")}
                    </button>
                    <button type="button" className="btn ripple btn-secondary" onClick={() => navigate("/admin/role/list/1")}>
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default RoleEdit;